import { type Menu } from '@/models/Menu';
import { useMenuApi } from '../api/useMenuApi';
import { useQuery } from '@tanstack/react-query';
import { type LanguageId } from '@/enums/enums';
import { type MenuAndChildParent } from '@/models/common/MenuChildAndParent';
import type { Option } from '@ids/react-hook-form';

export const useGetMenus = (languageId: LanguageId, locationCode: string) => {
  const { fetchMenus } = useMenuApi();
  return useQuery<Menu[], Error>({
    queryKey: ['menus', { languageId, locationCode }],
    queryFn: () => fetchMenus(languageId, locationCode),
  });
};

export const useGetMenusByParent = (languageId: LanguageId, parentId: number) => {
  const { fetchMenusByParent } = useMenuApi();
  return useQuery<Menu, Error>({
    queryKey: ['menusByParent', { languageId, parentId }],
    queryFn: () => fetchMenusByParent(languageId, parentId),
  });
};

export const useGetMenusByParentAndCategory = (
  languageId: LanguageId,
  parentId: number,
  categoryId: number,
) => {
  const { fetchMenusByParentAndCategory } = useMenuApi();
  return useQuery<Menu[], Error>({
    queryKey: ['menusByParent', { languageId, parentId, categoryId }],
    queryFn: () => fetchMenusByParentAndCategory(languageId, parentId, categoryId),
    enabled: !!categoryId,
  });
};

export const useGetParentAndChildMenu = (settingCode: string) => {
  const { fetchChildAndParentMenuBySettingAndMeuTypeCodes } = useMenuApi();
  return useQuery<MenuAndChildParent, Error>({
    queryKey: ['childAndParentMenu', { settingCode }],
    queryFn: () => fetchChildAndParentMenuBySettingAndMeuTypeCodes(settingCode),
    enabled: false,
  });
};

export const useGetParentMenus = (languageId: number) => {
  const { fetchParentMenus } = useMenuApi();
  return useQuery<Option[], Error>({
    queryKey: ['parentMenus', languageId],
    queryFn: () => fetchParentMenus(languageId),
  });
};
